import React, { useState, useEffect ,useRef} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";
import toast from "react-hot-toast";
import "./PagesAnnexes.css";
import { saveDossier } from "../../redux/dispatch";
import SaveLoader from "../shared/SaveLoader";
import { ThreeDots } from "react-loader-spinner";
const RUBRIQUE = "pages_annexes";

const MenuBar = ({ editor }) => {
  if (!editor) return null;
  const buttons = [
    { label: "G",       title: "Gras",           action: () => editor.chain().focus().toggleBold().run(),                  active: editor.isActive("bold"),           style: { fontWeight: "bold" } },
    { label: "I",       title: "Italique",        action: () => editor.chain().focus().toggleItalic().run(),                active: editor.isActive("italic"),         style: { fontStyle: "italic" } },
    { label: "S",       title: "Barré",           action: () => editor.chain().focus().toggleStrike().run(),                active: editor.isActive("strike"),         style: { textDecoration: "line-through" } },
    { label: "U",       title: "Souligné",        action: () => editor.chain().focus().toggleUnderline().run(),             active: editor.isActive("underline"),      style: { textDecoration: "underline" } },
    { label: "H1",      title: "Titre 1",         action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),   active: editor.isActive("heading", { level: 1 }) },
    { label: "H2",      title: "Titre 2",         action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),   active: editor.isActive("heading", { level: 2 }) },
    { label: "• List",  title: "Liste à puces",   action: () => editor.chain().focus().toggleBulletList().run(),            active: editor.isActive("bulletList") },
    { label: "1. List", title: "Liste numérotée", action: () => editor.chain().focus().toggleOrderedList().run(),           active: editor.isActive("orderedList") },
    { label: "❝",       title: "Citation",        action: () => editor.chain().focus().toggleBlockquote().run(),            active: editor.isActive("blockquote") },
    { label: "—",       title: "Séparateur",      action: () => editor.chain().focus().setHorizontalRule().run(),           active: false },
    { label: "↩",       title: "Annuler",         action: () => editor.chain().focus().undo().run(),                       active: false },
    { label: "↪",       title: "Rétablir",        action: () => editor.chain().focus().redo().run(),                       active: false },
  ];
  return (
    <div className="pa-menubar">
      {buttons.map((btn, i) => (
        <button key={i} title={btn.title}
          className={`pa-menubar-btn ${btn.active ? "pa-menubar-btn--active" : ""}`}
          style={btn.style || {}} onClick={btn.action} type="button">
          {btn.label}
        </button>
      ))}
    </div>
  );
};
const EXTENSIONS = [StarterKit, Underline];
const PageEditor = ({ content, onChange }) => {
  const editor = useEditor({
   // APRÈS
    extensions: EXTENSIONS,
    content: content || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "");
    }
  }, [content]);

  return (
    <div className="pa-editor-wrapper">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="pa-editor" />
    </div>
  );
};

const EMPTY_PAGE = () => ({ data: "" });

const PagesAnnexes = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { id }    = useParams();
const [saving, setSaving] = useState(false);
  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;
  const formulairesData  = useSelector((s) => s.formulaires.data);
  const savedPages = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.pages || [EMPTY_PAGE()]);
  const rawImages  = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || []);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];

  const [pages,      setPages]      = useState(savedPages);
  const [activePage, setActivePage] = useState(() => {
    try {
      const s = parseInt(sessionStorage.getItem(`pa_activePage_${id}`), 10);
      return Number.isInteger(s) && s >= 0 ? s : 0;
    } catch (e) { return 0; }
  });
  const [imgIndex,   setImgIndex]   = useState(0);
  const [imgLoading, setImgLoading] = useState(true);
  const [gotoValue,  setGotoValue]  = useState("");
  const imgRef = useRef(null);

  const pagesRef = useRef(pages);
  useEffect(() => { pagesRef.current = pages; }, [pages]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPages(savedPages);
    const s = parseInt(sessionStorage.getItem(`pa_activePage_${id}`), 10);
    setActivePage(Number.isInteger(s) && s >= 0 ? s : 0);
  }, [id]);

  useEffect(() => {
    setImgIndex(activePage < IMAGES.length ? activePage : IMAGES.length - 1);
  }, [activePage, IMAGES.length]);

  useEffect(() => {
    if (activePage >= pages.length) setActivePage(pages.length - 1);
  }, [pages.length, activePage]);

  // Persiste l'index de page courant (survit au changement de section / retour) — frontend only
  useEffect(() => {
    try { sessionStorage.setItem(`pa_activePage_${id}`, String(activePage)); } catch (e) {}
  }, [activePage, id]);

  // Réinitialise le spinner d'image à chaque changement d'image (gère le cas image déjà en cache)
  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) setImgLoading(false);
    else setImgLoading(true);
  }, [imgIndex]);

// APRÈS
  const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav(RUBRIQUE);
  const getPaginationItems = (total, current) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    const items = [];
    items.push(0);
    if (current > 3) items.push("...");
    for (let i = Math.max(1, current - 1); i <= Math.min(total - 2, current + 1); i++) {
      items.push(i);
    }
    if (current < total - 4) items.push("...");
    items.push(total - 1);
    return items;
  };

  const handleChangePage = (index, html) => {
    setPages((prev) => {
      const updated = [...prev];
      updated[index] = { data: html };
      return updated;
    });
  };

  const handleAddPage = () => {
    setPages((prev) => [...prev, EMPTY_PAGE()]);
    setActivePage(pages.length);
  };

  const handleDeletePage = (index) => {
    setPages((prev) => prev.filter((_, i) => i !== index));
    setActivePage((prev) => Math.max(0, prev - 1));
  };

  const handleGoto = () => {
    const n = parseInt(gotoValue, 10);
    if (Number.isInteger(n) && n >= 1 && n <= pages.length) {
      setActivePage(n - 1);
      setGotoValue("");
    }
  };

      const handleSave = () => {
        dispatch(updateFormData(id, RUBRIQUE, "pages", pagesRef.current));
      };

    useEffect(() => {
      return () => {
        handleSave();
      };
    }, []);

   const handleSaveAll = async () => {
    setSaving(true);
    dispatch(updateFormData(id, RUBRIQUE, "pages", pages));
    try {
        const result = await dispatch(saveDossier());
        toast.success(result.message);
        navigate("/");
      } catch (err) {
        toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
      }finally {
    setSaving(false);
  }
    
  };

  const handleCancel = () => setPages(savedPages);

  if (!dossier) {
    return (
      <div className="pa-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="pa-page">
       {saving && <SaveLoader />}
      <header className="pa-header">
        <div className="pa-header-left">
          <button className="pa-back-btn" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="pa-title">Dossier {dossier.numero_dossier || dossier.id}</h1>
            <p className="pa-subtitle">Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}</p>
          </div>
        </div>
        <button className="pa-save-btn" onClick={handleSaveAll} disabled={saving}>Sauvegarder les modifications</button>
      </header>

      <div className="pa-body">
        <aside className="pa-image-panel">
          <div className="pa-image-header">
            <span className="pa-rubrique-label">Pages Annexes</span>
            <span className="pa-image-counter">Image {imgIndex + 1} / {IMAGES.length}</span>
          </div>
          <div className="pa-image-viewer" style={{ position: "relative" }}>
            {imgLoading && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.6)", zIndex: 2 }}>
                <ThreeDots visible={true} height="60" width="60" color="#4fa94d" radius="9" ariaLabel="chargement-image" />
              </div>
            )}
            <img
              ref={imgRef}
              src={IMAGES[imgIndex]}
              alt={`Page ${imgIndex + 1}`}
              className="pa-image"
              onLoad={() => setImgLoading(false)}
              onError={() => setImgLoading(false)}
            />
          </div>

          <div className="pa-image-nav">
            <button className="pa-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
            {getPaginationItems(IMAGES.length, imgIndex).map((item, i) =>
              item === "..." ? (
                <span key={i} className="pa-ellipsis">…</span>
              ) : (
                <button key={i}
                  className={`pa-nav-dot-btn ${imgIndex === item ? "pa-nav-dot-btn--active" : ""}`}
                  onClick={() => setImgIndex(item)}>
                  {item + 1}
                </button>
              )
            )}
            <button className="pa-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
          </div>
        </aside>

        <main className="pa-form-panel">
          <div className="pa-form-scroll">

            {/* ── Pagination pages ── */}

            <div className="pa-pages-nav">
              <div className="pa-pages-tabs">
                <button className="pa-nav-page-btn" onClick={() => setActivePage((p) => Math.max(0, p - 1))} disabled={activePage === 0}>‹</button>
                {getPaginationItems(pages.length, activePage).map((item, i) =>
                  item === "..." ? (
                    <span key={i} className="pa-ellipsis">…</span>
                  ) : (
                    <button key={i}
                      className={`pa-page-tab ${activePage === item ? "pa-page-tab--active" : ""}`}
                      onClick={() => setActivePage(item)}>
                      {item + 1}
                    </button>
                  )
                )}
                <button className="pa-nav-page-btn" onClick={() => setActivePage((p) => Math.min(pages.length - 1, p + 1))} disabled={activePage === pages.length - 1}>›</button>
              </div>
              <div className="pa-goto" style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginLeft: "8px" }}>
                <input type="number" min={1} max={pages.length} placeholder="N°" value={gotoValue}
                  onChange={(e) => setGotoValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleGoto(); }}
                  style={{ width: "68px", padding: "5px 7px", border: "1px solid #cbd5e1", borderRadius: "6px", fontSize: "13px" }} />
                <button type="button" onClick={handleGoto}
                  style={{ padding: "5px 12px", border: "none", borderRadius: "6px", background: "#2563eb", color: "#fff", fontSize: "13px", cursor: "pointer", fontWeight: 600 }}>Aller</button>
                <span style={{ fontSize: "12px", color: "#64748b" }}>/ {pages.length}</span>
              </div>
              <button className="pa-add-page-btn" onClick={handleAddPage}>+ Ajouter une page</button>
            </div>

            {/* ── Éditeur ── */}
            <PageEditor
              key={activePage}
              content={pages[activePage]?.data || ""}
              onChange={(html) => handleChangePage(activePage, html)}
            />

            {/* ── Supprimer page ── */}
            {activePage > 0 && (
              <button className="pa-delete-page-btn" onClick={() => handleDeletePage(activePage)}>
                🗑 Supprimer cette page
              </button>
            )}

          </div>

          {/* <div className="pa-form-actions">
            <button className="pa-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="pa-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}


<RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />        </main>
      </div>
    </div>
  );
};

export default PagesAnnexes;